import React, { useState } from "react";
import { createPortal } from "react-dom";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import "./ContactModal.scss";

// Import icons
import { IoClose } from "react-icons/io5";
import { FaArrowRight } from "react-icons/fa6";
import { FaWhatsapp } from "react-icons/fa";
// Add import of the utility
import sendEmail from "../../../../utils/sendMail";

export default function ContactModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  // New state to track sending status
  const [sending, setSending] = useState(false);

  useGSAP(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    const isMobile = window.innerWidth <= 768;

    if (isOpen) {
      // Disable body scroll
      document.body.style.overflow = "hidden";

      // Animate modal in
      gsap.fromTo(
        ".contact-modal-overlay",
        { opacity: 0 },
        { opacity: 1, duration: isMobile ? 0.2 : 0.3, ease: "power2.out" }
      );
      gsap.fromTo(
        ".contact-modal-content",
        { x: "100%", opacity: 0 },
        {
          x: "0%",
          opacity: 1,
          duration: isMobile ? 0.3 : 0.5,
          ease: "power2.out",
          delay: isMobile ? 0.05 : 0.1,
        }
      );
    }

    // Cleanup function to ensure scroll is re-enabled
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, { dependencies: [isOpen], revertOnUpdate: true });

  const handleClose = () => {
    const isMobile = window.innerWidth <= 768;

    // Animate modal out
    gsap.to(".contact-modal-content", {
      x: "100%",
      opacity: 0,
      duration: isMobile ? 0.2 : 0.4,
      ease: "power2.in",
    });
    gsap.to(".contact-modal-overlay", {
      opacity: 0,
      duration: isMobile ? 0.2 : 0.3,
      delay: isMobile ? 0.1 : 0.2,
      ease: "power2.in",
      onComplete: () => {
        onClose();
      },
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);

    try {
      // Use the utility to send the email
      await sendEmail(formData);
      // Optionally clear form on success
      setFormData({ name: "", email: "", message: "" });
      // Close modal after successful send (preserves exit animation)
      handleClose();
    } catch (error) {
      // Minimal error handling — log and keep modal open for user correction
      console.error("Failed to send email:", error);
      // You may show an error UI here if desired
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="contact-modal-overlay" onClick={handleClose}>
      <div
        className="contact-modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-modal-heading"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-button" onClick={handleClose} aria-label="Close contact form">
          <IoClose />
        </button>

        <div className="modal-header">
          <h2 id="contact-modal-heading">Get in touch</h2>
        </div>

        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter your full name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your@email.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Enter your text"
              rows="6"
              required
            />
          </div>

          <div className="form-footer">
            <p className="privacy-text">
              By clicking the "Send" button, you agree to the processing of your
              personal data in accordance with our{" "}
              <a href="#" className="privacy-link">
                Privacy Policy
              </a>
            </p>

            {/* Button row for send and WhatsApp */}
            <div className="button-row">
              <button
                type="submit"
                className="send-container"
                disabled={sending}
                aria-busy={sending}
              >
                <div className="send-button">
                  {(sending ? "Sending..." : "Send")
                    .split("")
                    .map((char, i) => (
                      <span className="hero-button-letter" key={i}>
                        {char === " " ? "\u00A0" : char}
                      </span>
                    ))}
                </div>
                <div className="button-two" aria-hidden="true">
                  <span className="hero-button-letter">
                    <FaArrowRight />
                  </span>
                </div>
              </button>
              {/* "or" separator */}
              <span className="or-separator">or</span>
              {/* WhatsApp button */}
              <button
                type="button"
                className="whatsapp-button"
                onClick={() =>
                  window.open(
                    "https://wa.me/+254799966603", // Replace with your WhatsApp number
                    "_blank"
                  )
                }
                style={{
                  marginTop: "0",
                  width: "auto",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                WhatsApp <FaWhatsapp />
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  // Render modal as a portal to bypass the pinned container
  return createPortal(modalContent, document.body);
}
